import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { Search, MapPin } from "lucide-react";
import { Input } from "@/components/ui/input";
import { PathTrail } from "@/components/PathTrail";
import { ItemThumb } from "@/components/ItemCard";
import { api } from "@/lib/api";
import { useI18n } from "@/i18n/I18n";

export default function SearchPage() {
  const { t, fmtNum } = useI18n();
  const [q, setQ] = useState("");
  const [dq, setDq] = useState("");
  useEffect(() => { const h = setTimeout(() => setDq(q.trim()), 200); return () => clearTimeout(h); }, [q]);
  const { data, isFetching } = useQuery({
    queryKey: ["search", dq], enabled: dq.length >= 2,
    queryFn: () => api.get("/search", { params: { q: dq } }).then((r) => r.data), placeholderData: (p) => p,
  });
  const empty = data && dq.length >= 2 && !data.items.length && !data.locations.length;

  return (
    <div className="max-w-3xl mx-auto space-y-8">
      <div className="relative fade-up">
        <Search className="absolute start-4 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
        <Input data-testid="search-input" autoFocus value={q} onChange={(e) => setQ(e.target.value)} placeholder={t("search.placeholder")}
          className="h-16 ps-12 text-lg rounded-2xl border-2 border-slate-200 focus-visible:border-blue-500 bg-white shadow-sm" />
      </div>
      {dq.length < 2 && <p className="text-sm text-slate-400 text-center">{t("search.hint")}</p>}
      {empty && <p data-testid="search-no-results" className="text-center text-slate-500">{t("search.noResults", { q: dq })}</p>}
      {data && dq.length >= 2 && (
        <div className={`space-y-8 transition-opacity ${isFetching ? "opacity-60" : ""}`}>
          {data.items.length > 0 && (
            <ul className="space-y-3" data-testid="search-items">
              {data.items.map((it) => (
                <li key={it.id}>
                  <Link to={`/items/${it.id}`} data-testid={`search-result-${it.id}`} className="flex gap-4 rounded-2xl border border-slate-200 bg-white p-4 transition-[transform,box-shadow] duration-200 hover:-translate-y-0.5 hover:shadow-md">
                    <ItemThumb item={it} className="w-16 h-16" />
                    <div className="min-w-0 flex-1 space-y-2">
                      <p className="font-bold text-lg flex justify-between gap-2"><span className="truncate">{it.name}</span><span className="font-mono text-sm text-slate-400">×{fmtNum(it.quantity)}</span></p>
                      <PathTrail path={it.path} linkable={false} size="lg" emptyLabel={t("common.noLocation")} testId={`search-result-path-${it.id}`} />
                    </div>
                  </Link>
                </li>
              ))}
            </ul>
          )}
          {data.locations.length > 0 && (
            <section className="space-y-3">
              <h2 className="text-xs font-semibold uppercase tracking-wider text-slate-500">{t("search.locations")}</h2>
              <ul className="space-y-2">
                {data.locations.map((l) => (
                  <li key={l.id}><Link to={`/locations/${l.id}`} data-testid={`search-location-${l.id}`} className="flex items-center gap-3 rounded-xl border border-slate-200 bg-white p-3 hover:bg-slate-50">
                    <MapPin className="w-4 h-4 text-blue-600 shrink-0" /><PathTrail path={l.path} linkable={false} />
                  </Link></li>
                ))}
              </ul>
            </section>
          )}
        </div>
      )}
    </div>
  );
}
